import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  CloseOutlined,
  SearchOutlined,
  ArrowLeftOutlined,
  BookOutlined,
  RiseOutlined,
  DollarCircleOutlined,
  TeamOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import {
  KNOWLEDGE_ARTICLES,
  KNOWLEDGE_CATEGORIES,
  KnowledgeArticle,
  getArticleById,
} from '../../data/knowledgeBase';
import styles from './KnowledgeModal.module.css';
import { knowledgeBrandName } from '../../utils/miniAppBrand';

interface KnowledgeModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialArticleId?: string | null;
  /** Лендинг передаёт свою тему; внутри приложения интерфейс белый — тема светлая. */
  theme?: 'light' | 'dark';
}

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'products':
      return <RiseOutlined />;
    case 'ecosystem':
      return <DollarCircleOutlined />;
    case 'affiliates':
      return <TeamOutlined />;
    case 'about':
      return <FileTextOutlined />;
    default:
      return <BookOutlined />;
  }
};

export const KnowledgeModal: React.FC<KnowledgeModalProps> = ({
  isOpen,
  onClose,
  initialArticleId,
  theme = 'light',
}) => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'ru').startsWith('ru') ? 'ru' : 'en';
  const brandName = knowledgeBrandName;

  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeArticleId, setActiveArticleId] = useState<string>(
    initialArticleId || KNOWLEDGE_ARTICLES[0]?.id || ''
  );
  const [mobileDetailView, setMobileDetailView] = useState<boolean>(false);

  useEffect(() => {
    if (initialArticleId) {
      setActiveArticleId(initialArticleId);
      setMobileDetailView(true);
    } else if (KNOWLEDGE_ARTICLES.length > 0 && !activeArticleId) {
      setActiveArticleId(KNOWLEDGE_ARTICLES[0].id);
    }
  }, [initialArticleId, isOpen]);

  // Lock body & documentElement scroll when open
  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      setMobileDetailView(false);
      setSearchQuery('');
      setSelectedCategory('all');
    }
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const filteredArticles = useMemo(() => {
    return KNOWLEDGE_ARTICLES.filter((article) => {
      const matchesCategory =
        selectedCategory === 'all' || article.category === selectedCategory;

      if (!matchesCategory) return false;

      if (!searchQuery.trim()) return true;

      const q = searchQuery.toLowerCase();
      const title = (lang === 'ru' ? article.title.ru : article.title.en).toLowerCase();
      const desc = (
        lang === 'ru' ? article.shortDescription.ru : article.shortDescription.en
      ).toLowerCase();
      const tags = article.tags.join(' ').toLowerCase();

      return title.includes(q) || desc.includes(q) || tags.includes(q);
    });
  }, [selectedCategory, searchQuery, lang]);

  const activeArticle: KnowledgeArticle | undefined = useMemo(() => {
    return (
      getArticleById(activeArticleId) ||
      filteredArticles[0] ||
      KNOWLEDGE_ARTICLES[0]
    );
  }, [activeArticleId, filteredArticles]);

  if (!isOpen) return null;

  return createPortal(
    <div className={`${styles.overlay} ${theme === 'light' ? styles.light : ''}`} onClick={onClose}>
      <div
        className={styles.modalCard}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerLeft}>
            <div className={styles.headerIconWrapper}>
              <BookOutlined className={styles.headerIcon} />
            </div>
            <div>
              <h2 className={styles.headerTitle}>
                {lang === 'ru' ? `База знаний ${brandName}` : `${brandName} Knowledge Base`}
              </h2>
              <p className={styles.headerSubtitle}>
                {lang === 'ru'
                  ? 'Официальная документация, правила кредитования и механики платформы'
                  : 'Official documentation, loan rules and platform mechanics'}
              </p>
            </div>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
          >
            <CloseOutlined />
          </button>
        </div>

        {/* Search & Category Filter Bar */}
        <div className={`${styles.filterBar} ${mobileDetailView ? styles.hiddenOnMobile : ''}`}>
          <div className={styles.searchBox}>
            <SearchOutlined className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder={
                lang === 'ru'
                  ? 'Поиск по статьям (например, маржа, плечо, овернайт)...'
                  : 'Search docs (e.g. margin, leverage, overnight)...'
              }
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setMobileDetailView(false);
              }}
            />
            {searchQuery && (
              <button
                type="button"
                className={styles.clearSearchBtn}
                onClick={() => {
                  setSearchQuery('');
                  setMobileDetailView(false);
                }}
              >
                ✕
              </button>
            )}
          </div>

          <div className={styles.categoryTabs}>
            <button
              type="button"
              className={`${styles.categoryTab} ${
                selectedCategory === 'all' ? styles.categoryTabActive : ''
              }`}
              onClick={() => {
                setSelectedCategory('all');
                setMobileDetailView(false);
                if (KNOWLEDGE_ARTICLES[0]) {
                  setActiveArticleId(KNOWLEDGE_ARTICLES[0].id);
                }
              }}
            >
              {lang === 'ru' ? 'Все статьи' : 'All articles'}
            </button>
            {KNOWLEDGE_CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                type="button"
                className={`${styles.categoryTab} ${
                  selectedCategory === cat.id ? styles.categoryTabActive : ''
                }`}
                onClick={() => {
                  setSelectedCategory(cat.id);
                  setMobileDetailView(false);
                  const firstInCat = KNOWLEDGE_ARTICLES.find((a) => a.category === cat.id);
                  if (firstInCat) {
                    setActiveArticleId(firstInCat.id);
                  }
                }}
              >
                {lang === 'ru' ? cat.titleRu : cat.titleEn}
              </button>
            ))}
          </div>
        </div>

        {/* Modal Main Body: 2 Columns on Desktop, Switchable on Mobile */}
        <div className={styles.modalBody}>
          {/* Left Sidebar: Article List */}
          <div
            className={`${styles.articlesList} ${
              mobileDetailView ? styles.hiddenOnMobile : ''
            }`}
          >
            {filteredArticles.length === 0 ? (
              <div className={styles.emptyState}>
                <BookOutlined className={styles.emptyIcon} />
                <p>
                  {lang === 'ru'
                    ? 'Ничего не найдено по вашему запросу'
                    : 'No articles found matching your query'}
                </p>
              </div>
            ) : (
              filteredArticles.map((article) => {
                const isActive = article.id === activeArticle?.id;
                const title = lang === 'ru' ? article.title.ru : article.title.en;
                const desc =
                  lang === 'ru'
                    ? article.shortDescription.ru
                    : article.shortDescription.en;
                const categoryTitle =
                  lang === 'ru'
                    ? article.categoryTitle.ru
                    : article.categoryTitle.en;

                return (
                  <div
                    key={article.id}
                    className={`${styles.articleCard} ${
                      isActive ? styles.articleCardActive : ''
                    }`}
                    onClick={() => {
                      setActiveArticleId(article.id);
                      setMobileDetailView(true);
                    }}
                  >
                    <div className={styles.articleCardCategory}>
                      <span className={styles.articleCategoryIcon}>
                        {getCategoryIcon(article.category)}
                      </span>
                      <span>{categoryTitle}</span>
                    </div>
                    <div className={styles.articleCardTitle}>{title}</div>
                    <div className={styles.articleCardDesc}>{desc}</div>
                    <div className={styles.tagList}>
                      {article.tags.map((tag) => (
                        <span key={tag} className={styles.tagBadge}>
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Right Area: Article Reader */}
          <div
            className={`${styles.articleContentArea} ${
              !mobileDetailView ? styles.hiddenOnMobile : ''
            }`}
          >
            {/* Mobile Back Button */}
            <div className={styles.mobileDetailHeader}>
              <button
                type="button"
                className={styles.mobileBackBtn}
                onClick={() => setMobileDetailView(false)}
              >
                <ArrowLeftOutlined />{' '}
                {lang === 'ru' ? 'К списку статей' : 'Back to list'}
              </button>
            </div>

            {activeArticle ? (
              <div className={styles.readerContainer}>
                <div className={styles.readerMeta}>
                  <span className={styles.readerCategoryBadge}>
                    {getCategoryIcon(activeArticle.category)}{' '}
                    {lang === 'ru'
                      ? activeArticle.categoryTitle.ru
                      : activeArticle.categoryTitle.en}
                  </span>
                  <div className={styles.readerTags}>
                    {activeArticle.tags.map((tag) => (
                      <span key={tag} className={styles.readerTag}>
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>

                <h1 className={styles.readerTitle}>
                  {lang === 'ru'
                    ? activeArticle.title.ru
                    : activeArticle.title.en}
                </h1>

                <p className={styles.readerLead}>
                  {lang === 'ru'
                    ? activeArticle.shortDescription.ru
                    : activeArticle.shortDescription.en}
                </p>

                <hr className={styles.readerDivider} />

                <div className={styles.markdownContent}>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      a: ({ href, children }) => {
                        const id = href?.startsWith('#') ? href.slice(1) : '';
                        if (id && getArticleById(id)) {
                          return (
                            <button
                              type="button"
                              className={styles.articleJump}
                              onClick={() => {
                                setActiveArticleId(id);
                                setMobileDetailView(true);
                              }}
                            >
                              {children}
                            </button>
                          );
                        }
                        return (
                          <a href={href} target="_blank" rel="noopener noreferrer">
                            {children}
                          </a>
                        );
                      },
                    }}
                  >
                    {lang === 'ru'
                      ? activeArticle.contentRu
                      : activeArticle.contentEn}
                  </ReactMarkdown>
                </div>
              </div>
            ) : (
              <div className={styles.noArticleSelected}>
                <BookOutlined className={styles.emptyIcon} />
                <p>
                  {lang === 'ru'
                    ? 'Выберите статью из списка слева'
                    : 'Select an article from the list'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default KnowledgeModal;
